import { AutocompleteOption, InitialValue, LocationAutocomplete } from '@checkit/react-components';
import { FormPanel } from '@checkit/react-components';
import { yupResolver } from '@hookform/resolvers';
import { TextField } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { createLocation } from 'history';
import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';

import { CheckpointTypeDto } from '../../../checkpointTypes/state';
import { RouteLocationState } from '../../../common/WithQueryParams';
import { CheckpointTypeAutocomplete } from '../../../components/checkpointTypeAutocomplete/CheckpointTypeAutocomplete';
import RouteLeavingGuard from '../../../components/routeLeaving/routeLeaving';
import { checkpointSchema } from '../../schema';
import { Checkpoint, CheckpointDto } from '../../state';

import { messages } from './CheckpointForm.translations';
import { IdentitySection } from './components/IdentitySection';
import { TextInput } from './components/TextInput';
import { FormToolbar } from './FormToolbar';
import useWindowUnload from './hooks/usePreventWindowUnload';

export interface Props {
  onSubmit: (data: CheckpointDto) => Promise<AnyAction>;
  checkpoint?: Checkpoint | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  formInput: {
    marginTop: theme.spacing(0)
  }
}));

export const CheckpointForm = (props: Props) => {
  const { onSubmit, checkpoint } = props;

  const { formatMessage } = useIntl();

  const formMethods = useForm({
    resolver: yupResolver(checkpointSchema(formatMessage))
  });

  const {handleSubmit, control, formState, register} = formMethods;
  const errors: FieldErrors = formMethods.errors;
  const {setValue} = control;

  const setOrder = useCallback(
    (value: string) => {
      setValue('order', value, { shouldDirty: true, shouldValidate: true });
    }, [setValue]
  );

  const classes = useStyles();

  let description = '';
  let qrCodeId = '';
  let name = '';

  const [types, setTypes] = useState<CheckpointTypeDto[]>(checkpoint ? checkpoint.types : []);
  const [locationCode, setLocationCode] = useState<string | null>(
    checkpoint && checkpoint.location ? checkpoint.location.code : null);

  let locationInitial: InitialValue | null = null;
  const typeValue = types.map(type => ({ name: String(type.name), code: String(type.code) }));

  const previousState = useHistory().location.state as RouteLocationState;
  const previousLocation = previousState ? previousState.previousLocation : createLocation('/checkpoints');

  // Set existing form values
  if (checkpoint) {
    const location = checkpoint.location;
    description = checkpoint.description;
    qrCodeId = checkpoint.qrCodeId || '';
    name = checkpoint.name;

    if (location) {
      locationInitial = ({ name: location.name, code: location.code });
    }
  }

  const [ locationValue ] = useState(locationInitial);

  const [dirty, setDirty] = useState(false);

  useWindowUnload(dirty);

  const onFormChange = () => setDirty(true);

  const onFormSubmit = async (newCheckpoint: any) => {
    setDirty(false);

    const checkpointDto: CheckpointDto = {
      description: newCheckpoint.description,
      locationCode,
      name: newCheckpoint.name,
      qrCodeId: newCheckpoint.qrCodeId || null,
      sequenceNumber: newCheckpoint.order,
      types
    };

    await onSubmit(checkpointDto);
  };

  const onTypeChange = useCallback((event: React.ChangeEvent<{}>, value: AutocompleteOption[] | null) => {
    setTypes(value || []);
    setDirty(true);
  }, [setTypes]);

  const onLocationChange = useCallback((event: React.ChangeEvent<{}>, value: AutocompleteOption | null) => {
    setLocationCode(value ? value.code : null);
    setDirty(true);

    // Clear visit order if location de-selected
    if (!value) {
      setOrder('');
    }
  }, [setLocationCode, setOrder]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onFormSubmit)} onChange={onFormChange}>
        <FormPanel
          heading="Checkpoint"
          name="checkpoint"
          initialExpanded={true}
        >
          <TextField
            inputRef={register}
            defaultValue={name}
            id="name"
            name="name"
            label={formatMessage(messages['checkpoint.form.name'])}
            autoFocus={true}
            className={classes.formInput}
            autoComplete="off"
            fullWidth={true}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : ''}
          />
          <TextField
            inputRef={register}
            defaultValue={description}
            id="description"
            name="description"
            label={formatMessage(messages['checkpoint.form.description'])}
            className={classes.formInput}
            autoComplete="off"
            fullWidth={true}
            error={!!errors.description}
            helperText={errors.description ? errors.description.message : ''}
          />
          <CheckpointTypeAutocomplete
            multiple={true}
            className={classes.formInput}
            initialValue={typeValue}
            onChange={onTypeChange}
            fullWidth={true}
          />
        </FormPanel>
        <FormPanel
          heading="Location"
          name="checkpoint"
          initialExpanded={true}
        >
          <LocationAutocomplete
            onChange={onLocationChange}
            initialValue={locationValue || undefined}
            className={classes.formInput}
            fullWidth={true}
          />
          <TextInput
            name="order"
            label={formatMessage(messages['checkpoint.form.order'])}
            initialValue={checkpoint && checkpoint.sequenceNumber ? String(checkpoint.sequenceNumber) : undefined}
            disabled={locationCode === null}
            className={classes.formInput}
          />
        </FormPanel>
        <IdentitySection
          className={classes.formInput}
          initialValue={qrCodeId}
        />
        <FormToolbar to={previousLocation} formSubmitting={formState.isSubmitting} />
      </form>
      <RouteLeavingGuard when={dirty} />
    </FormProvider>
  );
};

export default CheckpointForm;
